import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, X, ChevronLeft, ChevronRight, Upload, Paintbrush, Loader2, Wand2 } from 'lucide-react';
import { useColor, useTheme, useBrand } from '../../context/ColorContext';
import axios from 'axios';
import api from '../../services/api';
import { toast } from 'sonner';
import BeforeAfterSlider from '../ui/BeforeAfterSlider';

// Helper: determine if text should be light or dark based on bg color
const isLightColor = (hex) => {
    if (!hex) return true;
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

const VisualizerModal = ({ isOpen, onClose, aiPrompt: kartelaPrompt }) => {
    const { activeColor } = useColor();
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';
    const [isClosing, setIsClosing] = useState(false);

    const [images, setImages] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const [userImage, setUserImage] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isProcessingAI, setIsProcessingAI] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;

        const fetchImages = async () => {
            try {
                setIsLoading(true);
                const res = await axios.get('/api/home-visualizer');
                setImages(res.data);
            } catch (error) {
                console.error('Home visualizer images fetch error:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchImages();
    }, [isOpen]);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setUserImage(null);
            onClose();
        }, 300);
    }, [onClose]);

    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget) handleClose();
    }, [handleClose]);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setUserImage(ev.target.result);
            setProcessedImage(null);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    }, []);

    const processImageWithAI = async (base64Img, targetColor) => {
        if (!base64Img) return;
        setIsProcessingAI(true);
        toast.info('Yapay zeka odanızı boyuyor. Lütfen bekleyin...', { duration: 5000 });

        try {
            const defaultPrompt = `Paint the walls of this room with color hex ${targetColor.code}. Keep the furniture, lighting, and textures realistic and intact. Only change the wall paint color.`;
            const prompt = kartelaPrompt
                ? kartelaPrompt.replace(/\{color\}/g, targetColor.code)
                : defaultPrompt;

            const res = await api.post('/fal-ai/edit', {
                prompt: prompt,
                image_url: base64Img
            });

            if (res.data && res.data.image && res.data.image.url) {
                setProcessedImage(res.data.image.url);
                toast.success('Odanız başarıyla boyandı!');
            } else if (res.data && res.data.image_url) {
                setProcessedImage(res.data.image_url);
                toast.success('Odanız başarıyla boyandı!');
            } else if (res.data && res.data.images && res.data.images[0]?.url) {
                setProcessedImage(res.data.images[0].url);
                toast.success('Odanız başarıyla boyandı!');
            } else {
                console.error("Beklenmeyen API Yanıtı:", res.data);
                throw new Error("Geçersiz API yanıtı.");
            }
        } catch (error) {
            console.error("AI İşleme Hatası:", error);
            const errMsg = error.response?.data?.error || "Oda boyanırken bir hata oluştu. Ayarlardan API anahtarını kontrol edin.";
            toast.error(errMsg, { duration: 6000 });
        } finally {
            setIsProcessingAI(false);
        }
    };

    const handleManualAITrigger = () => {
        if (userImage && !isProcessingAI && !isLoading) {
            processImageWithAI(userImage, activeColor);
        } else if (!userImage) {
            toast.error("Lütfen önce kendi odanızı yükleyin.");
        }
    };

    const nextImage = () => setActiveIndex((prev) => (prev + 1) % images.length);
    const prevImage = () => setActiveIndex((prev) => (prev - 1 + images.length) % images.length);

    if (!isOpen) return null;

    const brandPrimaryColor = activeBrand.primaryColor;
    const light = isLightColor(activeColor.code);

    // Default placeholder if nothing loaded and no user image
    const placeholderImage = 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80';
    const hasImages = images.length > 0;
    const roomImage = hasImages ? images[activeIndex]?.url : placeholderImage;

    const displayImage = processedImage || userImage || roomImage;

    return (
        <div
            className={`fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-8 ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={handleBackdropClick}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-xl" />

            {/* Modal */}
            <div className={`relative z-10 w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-[2rem] shadow-2xl flex flex-col lg:flex-row ${isDark ? 'bg-slate-900' : 'bg-white'} ${isClosing ? 'animate-zoomOut' : 'animate-zoomIn'}`}>

                {/* Close button */}
                <button
                    onClick={handleClose}
                    className={`absolute top-4 right-4 z-30 p-2.5 rounded-xl transition-all active:scale-90 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-900'}`}
                >
                    <X size={20} />
                </button>

                {/* Left / Top - Visual Area */}
                <div className="relative flex-1 min-h-[250px] lg:min-h-[450px] overflow-hidden transition-colors duration-500 group" style={{ backgroundColor: activeColor.code }}>
                    {/* Noise texture */}
                    <div className="absolute inset-0 opacity-[0.04] bg-[url('/noise.svg')] mix-blend-overlay pointer-events-none z-10" />

                    {isProcessingAI ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center z-[25] gap-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                            <Loader2 size={64} className="animate-spin text-white" style={{ color: brandPrimaryColor }} />
                            <div className="flex flex-col items-center">
                                <h4 className="text-white font-black uppercase tracking-widest text-sm mb-1">DÜFA AI ÇALIŞIYOR</h4>
                                <p className="text-white/70 text-[10px] font-bold tracking-wider">{activeColor.name} rengi odanıza uygulanıyor...</p>
                            </div>
                        </div>
                    ) : isLoading && !userImage ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center z-[5] gap-3">
                            <Loader2 size={48} className={`animate-spin ${light ? 'text-black/20' : 'text-white/20'}`} />
                        </div>
                    ) : displayImage ? (
                        <>
                            {userImage && processedImage ? (
                                <div className="absolute inset-0 z-[5]">
                                    <BeforeAfterSlider beforeImage={userImage} afterImage={processedImage} />
                                </div>
                            ) : (
                                <img
                                    src={displayImage}
                                    alt="Oda Görseli"
                                    className="absolute inset-0 w-full h-full object-cover z-[5] pointer-events-none select-none"
                                />
                            )}

                            {/* Navigation Arrows (only if showing gallery, not user image) */}
                            {hasImages && images.length > 1 && !userImage && (
                                <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-all opacity-0 group-hover:opacity-100"
                                    >
                                        <ChevronLeft size={24} />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-all opacity-0 group-hover:opacity-100"
                                    >
                                        <ChevronRight size={24} />
                                    </button>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center z-[5] gap-3">
                            <Camera size={48} className={`${light ? 'text-black/20' : 'text-white/20'}`} />
                            <p className={`text-xs font-bold uppercase tracking-widest ${light ? 'text-black/30' : 'text-white/30'}`}>
                                Görsel Seçin veya Yükleyin
                            </p>
                        </div>
                    )}

                    {/* Color name overlay at bottom */}
                    <div className="absolute bottom-0 inset-x-0 z-20 p-4 bg-gradient-to-t from-black/40 to-transparent">
                        <p className="text-white/80 text-[10px] font-black uppercase tracking-widest drop-shadow-lg">
                            {activeColor.name}
                        </p>
                    </div>
                </div>

                {/* Right / Bottom - Control Panel */}
                <div className={`w-full lg:w-[340px] shrink-0 flex flex-col p-6 lg:p-8 gap-6 overflow-y-auto ${isDark ? 'border-t lg:border-t-0 lg:border-l border-white/5' : 'border-t lg:border-t-0 lg:border-l border-black/5'}`}>

                    {/* Header */}
                    <div>
                        <span className="text-[9px] font-black uppercase tracking-[0.3em] opacity-40">Odanı Canlandır</span>
                        <h3 className={`text-lg font-black uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {activeBrand?.name || 'DÜFA AI'} İLHAMI
                        </h3>
                    </div>

                    {/* Selected Color Info */}
                    <div className={`p-4 rounded-2xl flex items-center gap-4 ${isDark ? 'bg-white/5 border border-white/5' : 'bg-slate-50 border border-black/5'}`}>
                        <div
                            className="w-14 h-14 rounded-xl shadow-lg border-4 shrink-0 transition-colors duration-500"
                            style={{
                                backgroundColor: activeColor.code,
                                borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.9)'
                            }}
                        />
                        <div className="min-w-0 flex-1">
                            <span className={`text-[9px] font-black uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Seçili Renk</span>
                            <p className={`text-sm font-black uppercase tracking-tight truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {activeColor.name}
                            </p>
                            <p className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                {activeColor.code}
                            </p>
                        </div>
                    </div>

                    {/* Upload / AI Buttons */}
                    {userImage ? (
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={handleManualAITrigger}
                                disabled={isProcessingAI}
                                className={`w-full py-4 rounded-xl font-black uppercase tracking-[0.15em] text-white text-[11px] shadow-xl hover:brightness-110 active:scale-[0.97] transition-all flex items-center justify-center gap-2.5 ${isProcessingAI ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                                style={{ backgroundColor: brandPrimaryColor }}
                            >
                                {isProcessingAI ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                                {isProcessingAI ? 'İŞLENİYOR...' : 'RENK UYGULA'}
                            </button>
                            <button
                                onClick={() => {
                                    setUserImage(null);
                                    setProcessedImage(null);
                                }}
                                disabled={isProcessingAI}
                                className={`w-full py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] border active:scale-[0.97] transition-all flex items-center justify-center gap-2 ${isDark ? 'border-white/20 text-white/70 hover:bg-white/5' : 'border-black/20 text-slate-600 hover:bg-slate-50'}`}
                            >
                                Başka Oda Yükle
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isProcessingAI}
                            className={`w-full py-4 rounded-xl font-black uppercase tracking-[0.15em] text-white text-[11px] shadow-xl hover:brightness-110 active:scale-[0.97] transition-all flex items-center justify-center gap-2.5 ${isProcessingAI ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                            style={{ backgroundColor: brandPrimaryColor }}
                        >
                            <Upload size={16} />
                            Fotoğraf Yükle / Çek
                        </button>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/png,image/*"
                        onChange={handleFileChange}
                    />

                    {/* Image indicator dots (if showing gallery) */}
                    {hasImages && images.length > 1 && !userImage && (
                        <div className="flex justify-center gap-2 mt-2">
                            {images.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveIndex(idx)}
                                    className={`w-2 h-2 rounded-full transition-all ${idx === activeIndex ? 'w-6' : 'bg-slate-300 dark:bg-white/10'}`}
                                    style={{ backgroundColor: idx === activeIndex ? brandPrimaryColor : undefined }}
                                />
                            ))}
                        </div>
                    )}

                    {/* Hint */}
                    <p className={`text-[10px] font-medium leading-relaxed text-center mt-auto ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                        Mekanınızın veya örnek odanın üzerinde seçtiğiniz rengin yansımasını hızlı bir şekilde test edebilirsiniz.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VisualizerModal;
